/**
 * CreateMemberModal - 회원을 새로 등록하는 모달 컴포넌트
 *
 * - 이름(필수), 연락처(필수) 입력 폼 제공
 * - 센터는 부모(현재는 AdminMembersListPage)가 URL의 centerId를 통해 결정해 centerName을 전달
 * - useCreateMember 훅을 사용하여 Cloud Function `createMember`를 호출
 * - 생성 성공 시 onCreated 콜백 호출 → 부모가 모달을 닫고 목록을 refetch
 */

import { useState, useEffect } from "react";
import type { FormEvent } from "react";
import { useCreateMember } from "@/hooks/useMembers";
import type { CreateMemberParams } from "@/hooks/useMembers";

interface CreateMemberModalProps {
  isOpen: boolean;
  /** 현재 페이지에서 결정된 센터 이름. 백엔드 lookupCenterByName의 입력으로 사용됨. */
  centerName: string;
  onClose: () => void;
  onCreated: () => void;
}

/** 연락처를 숫자만 추출한 정규화된 값으로 변환 */
function normalizeMobile(input: string): string {
  return input.replace(/[^0-9]/g, "");
}

/** 백엔드 contract와 동일한 길이 규칙(10~11자리) */
function isValidMobile(normalized: string): boolean {
  return normalized.length === 10 || normalized.length === 11;
}

/** mutation.error의 message를 안전하게 추출 */
function getErrorMessage(error: unknown): string | null {
  if (error && typeof error === "object" && "message" in error) {
    const msg = (error as { message: unknown }).message;
    if (typeof msg === "string" && msg.length > 0) {
      return msg;
    }
  }
  return null;
}

export function CreateMemberModal({
  isOpen,
  centerName,
  onClose,
  onCreated,
}: CreateMemberModalProps) {
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);

  const createMember = useCreateMember();

  // 모달이 열릴 때 폼 초기화
  useEffect(() => {
    if (isOpen) {
      setName("");
      setMobile("");
      setValidationError(null);
      createMember.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    const trimmedName = name.trim();
    const normalizedMobile = normalizeMobile(mobile);

    if (!trimmedName) {
      setValidationError("이름을 입력해주세요.");
      return;
    }

    if (!normalizedMobile) {
      setValidationError("연락처를 입력해주세요.");
      return;
    }

    if (!isValidMobile(normalizedMobile)) {
      setValidationError("연락처는 숫자 10~11자리로 입력해주세요.");
      return;
    }

    setValidationError(null);

    const params: CreateMemberParams = {
      centerName,
      name: trimmedName,
      mobile: normalizedMobile,
    };

    createMember.mutate(params, {
      onSuccess: () => {
        onCreated();
      },
    });
  };

  const isSubmitDisabled = !name.trim() || !mobile.trim() || createMember.isPending;
  const serverErrorMessage = createMember.isError ? getErrorMessage(createMember.error) : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">새 회원 등록</h2>
        <p className="text-sm text-gray-500 mb-4">센터: {centerName}</p>

        <form onSubmit={handleSubmit}>
          {/* 이름 (필수) */}
          <div className="mb-4">
            <label htmlFor="memberName" className="block text-sm font-medium text-gray-700 mb-1">
              이름 <span className="text-red-500">*</span>
            </label>
            <input
              id="memberName"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          {/* 연락처 (필수) */}
          <div className="mb-4">
            <label htmlFor="memberMobile" className="block text-sm font-medium text-gray-700 mb-1">
              연락처 <span className="text-red-500">*</span>
            </label>
            <input
              id="memberMobile"
              type="tel"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder="예: 010-1234-5678"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          {/* 클라이언트 검증 에러 */}
          {validationError && <p className="text-sm text-red-600 mb-4">{validationError}</p>}

          {/* 서버 에러 (백엔드 한글 메시지 그대로 노출) */}
          {createMember.isError && !validationError && (
            <p className="text-sm text-red-600 mb-4">
              {serverErrorMessage ?? "회원 등록에 실패했습니다. 다시 시도해주세요."}
            </p>
          )}

          {/* 버튼 영역 */}
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={isSubmitDisabled}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {createMember.isPending ? "등록 중..." : "등록"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
